import { sleep, wrapPromiseInTimeout } from '../../helpers/AsyncHelpers';

interface IFrontEndLogger {
  notify: (error: Error) => void;
  leaveBreadcrumb: (message: string, metadata?: object) => void;
  debug: (message: string, metadata?: object) => void;
}

export interface IWorkoutCacheEntry {
  index: number;
  indexesThatAreDuplicates: number[];
  name: string;
  cachingProcessIdentifier: number;
  startCache(): Promise<void>;
}

export interface ICachingSetupParams {
  waitBetweenStartsInMs: number;
  numberOfEntriesToConcurrentlyCache: number;
  retryWaitInMsBeforeAddingBackToQueue: number;
  entryTimeoutInMs: number;
  highestIndexThatMustSucceed: number;
  allowedRetryCountOfIndexesThatMustSucceed: number;
  cachingProcessIdentifier: number;
}

// this class makes sure:
// - that a number amount of downloads are in progress a once to speed up downloading
// - that multiples don't start at once to prevent resource locks
// - that failed entries are retried
// - that critical failures abort the process
// - that successes on each entry, or overall aborts are called back
export class HomeWorkoutConcurrentCachingAlgo {
  public static requestProcessingToStop() {
    HomeWorkoutConcurrentCachingAlgo.currentCachingProcessIdentifier = 0;
  }

  public static setupDefaultParams(
    currentIndexToStartCachingFrom: number,
    numberOfEntriesThatMustSucceed: number,
  ): ICachingSetupParams {
    return {
      waitBetweenStartsInMs: 100,
      numberOfEntriesToConcurrentlyCache: 1,
      retryWaitInMsBeforeAddingBackToQueue: 5000, // don't immediately retry, to give network etc time to resolve
      entryTimeoutInMs: 60000, // one minute should be more than enough for an entry
      highestIndexThatMustSucceed: currentIndexToStartCachingFrom + numberOfEntriesThatMustSucceed,
      allowedRetryCountOfIndexesThatMustSucceed: 10,
      cachingProcessIdentifier: Date.now(),
    };
  }

  private static currentCachingProcessIdentifier = 0;

  private cacheEntriesInProgress = new Set<IWorkoutCacheEntry>();

  private cacheEntriesPendingRetry = new Set<IWorkoutCacheEntry>();

  private retriesDoneAlready = 0;

  public constructor(
    private readonly setupParams: ICachingSetupParams,
    public cacheEntriesUnprocessedQueue: IWorkoutCacheEntry[],
    private readonly logger: IFrontEndLogger,
    private readonly entryCachedSuccessfullyCallback: (indexes: number[] | number) => void,
    private readonly criticalCachingAbortCallback: (index: number) => void,
  ) {
    this.cacheEntriesUnprocessedQueue = cacheEntriesUnprocessedQueue;
    HomeWorkoutConcurrentCachingAlgo.currentCachingProcessIdentifier = setupParams.cachingProcessIdentifier;
  }

  public async processAllConcurrently() {
    while (this.shouldContinueProcessing()) {
      // wait to avoid potential resource conflicts and make the loop not spin
      await sleep(this.setupParams.waitBetweenStartsInMs);
      this.processNext();
    }
  }

  public processNext() {
    if (this.isMaxConcurrentNumberAlreadyInProgress()) {
      return;
    }

    const nextEntry = this.removeFromStartOfUnprocessedQueue();
    if (!nextEntry) {
      this.logger.debug('No entries waiting to caching, but there might be in progress.');
      return;
    }

    if (this.processStopWasRequested(nextEntry)) {
      this.logger.leaveBreadcrumb('Caching process ended due to stop request or new workout started');
      this.stopProcessing();
      return;
    }

    this.logger.debug('Trying to cache entry if not already', nextEntry);
    this.startCaching(nextEntry);
  }

  public getCacheEntriesInProgress() {
    return [...this.cacheEntriesInProgress];
  }

  public shouldContinueProcessing(): boolean {
    const queueEmpty = !this.cacheEntriesUnprocessedQueue || this.cacheEntriesUnprocessedQueue.length < 1;
    const inProgressEmpty = this.cacheEntriesInProgress.size < 1;
    const pendingRetriesEmpty = this.cacheEntriesPendingRetry.size < 1;
    if (queueEmpty && inProgressEmpty && pendingRetriesEmpty) {
      this.logger.leaveBreadcrumb(
        'Caching process completed, no entries left.'
        + `In progress:${this.cacheEntriesInProgress.size}, waiting:${this.cacheEntriesUnprocessedQueue.length}`,
      );
      return false;
    }
    return true;
  }

  private startCaching(nextEntry: IWorkoutCacheEntry) {
    this.cacheEntriesInProgress.add(nextEntry);
    const startCachePromise = nextEntry.startCache();
    const startCacheOrTimeout = wrapPromiseInTimeout(
      startCachePromise,
      this.setupParams.entryTimeoutInMs,
      `${nextEntry.name} timed out after ${this.setupParams.entryTimeoutInMs}ms`,
    );

    startCacheOrTimeout
      .then(() => {
        this.cacheEntriesInProgress.delete(nextEntry);
        const indexes = nextEntry.indexesThatAreDuplicates
          ? [nextEntry.index, ...nextEntry.indexesThatAreDuplicates]
          : [nextEntry.index];
        this.logger.debug('Successfully cached entry', nextEntry);
        this.entryCachedSuccessfullyCallback(indexes);
      })
      .catch((error) => {
        if (this.shouldAbortDueToMandatoryEntryFailing(nextEntry)) {
          this.logger.leaveBreadcrumb('Aborting caching', {
            details: 'due to mandatory entry failing',
            entry: nextEntry.name,
            error: error.message,
          });
          this.abortDueToCriticalError(nextEntry);
          this.logDetailedError(error, this.logger);
        } else {
          this.logger.leaveBreadcrumb('Queuing cache retry', {
            error: error.message,
            retriesDone: this.retriesDoneAlready + 1,
            ...nextEntry,
          });
          this.addBackToQueueForRetry(nextEntry);
        }
      });
  }

  private stopProcessing() {
    this.cacheEntriesUnprocessedQueue = [];
  }

  private removeFromStartOfUnprocessedQueue() {
    return this.cacheEntriesUnprocessedQueue.shift();
  }

  private isMaxConcurrentNumberAlreadyInProgress() {
    return this.cacheEntriesInProgress.size >= this.setupParams.numberOfEntriesToConcurrentlyCache;
  }

  private processStopWasRequested(nextEntry: IWorkoutCacheEntry) {
    return nextEntry.cachingProcessIdentifier !== HomeWorkoutConcurrentCachingAlgo.currentCachingProcessIdentifier;
  }

  private addBackToQueueForRetry(nextEntry: IWorkoutCacheEntry) {
    this.cacheEntriesPendingRetry.add(nextEntry);
    this.cacheEntriesInProgress.delete(nextEntry);
    setTimeout(() => {
      this.retriesDoneAlready += 1;
      this.cacheEntriesPendingRetry.delete(nextEntry);
      this.cacheEntriesUnprocessedQueue.unshift(nextEntry);
    }, this.setupParams.retryWaitInMsBeforeAddingBackToQueue);
  }

  private shouldAbortDueToMandatoryEntryFailing(nextEntry: IWorkoutCacheEntry) {
    return this.isMandatoryEntry(nextEntry)
        && this.retriesDoneAlready >= this.setupParams.allowedRetryCountOfIndexesThatMustSucceed;
  }

  private isMandatoryEntry(nextEntry: IWorkoutCacheEntry) {
    return nextEntry.index <= this.setupParams.highestIndexThatMustSucceed;
  }

  private abortDueToCriticalError(nextEntry: IWorkoutCacheEntry) {
    HomeWorkoutConcurrentCachingAlgo.requestProcessingToStop();
    this.cacheEntriesInProgress.delete(nextEntry);
    this.stopProcessing();
    this.criticalCachingAbortCallback(nextEntry.index);
  }

  private logDetailedError(error: any, logger: IFrontEndLogger) {
    const fullError = new Error(JSON.stringify(error, Object.getOwnPropertyNames(error)));
    logger.notify(fullError);
  }
}
